import React, {createContext, PropsWithChildren, useEffect, useMemo, useState} from 'react';
import {useLocation} from "react-router-dom";
import {auth as firebaseAuth} from "../../libs/firebase/firebase";
import {userApi} from "../../apis/admin/user/userApi";
import {UserType} from "../../enum/user/userType";
import {SalesmanType} from "../../enum/user/salesmanType";
import {LoadingPage} from "../../pages/LoadingPage";
import {config} from "../../libs/config/config";
import {isNeedAuthRoute, matchRoute} from "../../routes";
import useAppRouter from "../../hooks/useAppRouter";
import {InvalidCredentialError} from "../../errors/InvalidCredentialError";
import {TooManyAuthRequestError} from "../../errors/TooManyAuthRequestError";

type AuthStateType = {
  isInitialized: boolean,
  isAuthenticated: boolean,
  currentUser: User |null,
}

type AuthContextType = {
  state: AuthStateType,
  login(email: string, password: string): Promise<void>;
  logout(): Promise<void>;
  getIdToken(): Promise<string | null>;
  reloadCurrentUser(): Promise<void>;
};

export type User = {
  name: string;
  companyName: string;
  email: string;
  userType: UserType;
  adminFlg: SalesmanType | null;
  roomId: number | null;
  roomName: string | null;
  accessRoomIds: number[];
  extraCustomer: {
    zip: string;
    address: string;
    building: string;
    tel: string;
    guestFlg: boolean;
  } | null;
  extraSalesman: {
    department: string;
    employeeNumber: string;
  } | null;
};

export const AuthContext = createContext<AuthContextType>({
  state: {
    isInitialized: false,
    isAuthenticated: false,
    currentUser: null,
  },
  login: (): Promise<void> => {
    throw new Error('Not implemented');
  },
  logout: (): Promise<void> => {
    throw new Error('Not implemented');
  },
  getIdToken: (): Promise<string | null> => {
    throw new Error('Not implemented');
  },
  reloadCurrentUser: (): Promise<void> => {
    throw new Error('Not implemented');
  },
});

export const AuthProvider: React.FC = (props: PropsWithChildren<any>) => {
  const { children } = props;
  const [abortController, setAbortController] = useState<AbortController | undefined>();
  const location = useLocation();
  const { goto } = useAppRouter();
  const [authState, setAuthState] = useState<AuthStateType>({
    isInitialized: false,
    isAuthenticated: false,
    currentUser: null,
  });

  const getUser = async (): Promise<User | null> => {
    setAbortController(new AbortController());
    const response = await userApi.show({
      abortSignal: abortController?.signal,
    });
    setAbortController(undefined);
    return response.user ?? null;
  };

  /**
   * ログイン
   * @param email
   * @param password
   */
  const login = async (email: string, password: string): Promise<void> => {
    try {
      await firebaseAuth.signInWithEmailAndPassword(email, password);
    } catch (e) {
      if ((e.code === 'auth/invalid-email')
        || (e.code === 'auth/user-disabled')
        || (e.code === 'auth/user-not-found')
        || (e.code === 'auth/wrong-password')) {
        throw new InvalidCredentialError('メールアドレス、またはパスワードが間違っています');
      } else if ((e.code === 'auth/too-many-requests')) {
        throw new TooManyAuthRequestError('ログインが規定回数以上試みられたため、ログインを制限しました。しばらく経ってから再度お試しください');
      }

      throw e;
    }
  };

  /**
   * ログアウト
   */
  const logout = async (): Promise<void> => {
    await firebaseAuth.signOut();
  };

  /**
   * IDトークンの取得
   */
  const getIdToken = async (): Promise<string | null> => {
    const idToken = await firebaseAuth.currentUser?.getIdToken();
    return idToken ?? null;
  };

  /**
   * ゲストユーザーか否か
   */
  const isGuestUser = () => {
    return !!authState.currentUser
      && (authState.currentUser.userType === UserType.CUSTOMER)
      && !!authState.currentUser.extraCustomer
      && authState.currentUser.extraCustomer.guestFlg;
  };

  useEffect(() =>
    firebaseAuth.onAuthStateChanged(async (firebaseUser) => {
      if (firebaseUser) {
        try {
          const user = await getUser();

          setAuthState(prevState => {
            return {
              ...prevState,
              isInitialized: true,
              isAuthenticated: true,
              currentUser: user,
            }
          });

        } catch {
          await logout();
          goto('internalServerError');
        }
      } else {
        setAuthState(prevState => {
          return {
            ...prevState,
            isInitialized: true,
            isAuthenticated: false,
            currentUser: null,
          }
        });
      }
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  , []);

  const contextValue: AuthContextType = {
    state: useMemo(() => authState, [authState]) ,
    login,
    logout,
    getIdToken,
    reloadCurrentUser: async (): Promise<void> => {
      const user = await getUser();
      setAuthState(prevState => {
        return {
          ...prevState,
          currentUser: user,
        }
      });
    }
  };

  const isLoginPageAccessWithAuthenticated = (): boolean => {
    return authState.isAuthenticated &&
      (matchRoute(location.pathname, 'login'));
  }

  const isNeedAuthPageAccessWithNotAuthenticated = (): boolean => {
    return !authState.isAuthenticated && isNeedAuthRoute(location.pathname)
  }

  const isCustomerPageAccessBySalesAdminWithAuthenticated = (): boolean => {
    return authState.isAuthenticated
      && (
        matchRoute(location.pathname, 'roomSelect')
        || matchRoute(location.pathname, 'taskList')
        || matchRoute(location.pathname, 'roomAttachFile')
        || matchRoute(location.pathname, 'roomMember')
      )
      && (
        (authState.currentUser?.userType === UserType.SALESMAN)
        && (authState.currentUser?.adminFlg === SalesmanType.ADMIN)
      );
  }

  const isSalesPageAccessByCustomerWithAuthenticated = (): boolean => {
    return authState.isAuthenticated
      && (
        matchRoute(location.pathname, 'menuMaterialList')
        || matchRoute(location.pathname, 'menuMaterialCreate')
        || matchRoute(location.pathname, 'menuMaterialEdit')
        || matchRoute(location.pathname, 'labelSetList')
        || matchRoute(location.pathname, 'labelSetCreate')
        || matchRoute(location.pathname, 'labelSetEdit')
        || matchRoute(location.pathname, 'menuSetList')
        || matchRoute(location.pathname, 'menuSetCreate')
        || matchRoute(location.pathname, 'menuSetEdit')
        || matchRoute(location.pathname, 'roomList')
        || matchRoute(location.pathname, 'roomCreate')
        || matchRoute(location.pathname, 'roomEdit')
        || matchRoute(location.pathname, 'roomMemberEdit')
        || matchRoute(location.pathname, 'customerList')
        || matchRoute(location.pathname, 'salesmanList')
        || matchRoute(location.pathname, 'adminList')
      )
      && (authState.currentUser?.userType === UserType.CUSTOMER);
  }

  const isAdminPageAccessBySalesWithAuthenticated = (): boolean => {
    return authState.isAuthenticated
      && (
        matchRoute(location.pathname, 'roomCreate')
        || matchRoute(location.pathname, 'adminList')
        || matchRoute(location.pathname, 'customerList')
        || matchRoute(location.pathname, 'salesmanList')
      )
      && (
        (authState.currentUser?.userType === UserType.SALESMAN)
        && (authState.currentUser?.adminFlg !== SalesmanType.ADMIN)
      );
  }

  const isLoading = (): boolean => {
    return !authState.isInitialized
      || isLoginPageAccessWithAuthenticated()
      || isNeedAuthPageAccessWithNotAuthenticated()
      || isCustomerPageAccessBySalesAdminWithAuthenticated()
      || isSalesPageAccessByCustomerWithAuthenticated()
      || isAdminPageAccessBySalesWithAuthenticated();
  }

  useEffect(() => {
    // ToDo: 将来的にアクセス権限確認の役割はAPIに移譲する
    if (authState.isInitialized) {
      if (authState.isAuthenticated && isGuestUser()) {
        logout().then(() => goto('notFoundError', { goto: 'off' }));
      } else if (isLoginPageAccessWithAuthenticated()) {
        switch (authState.currentUser?.userType) {
          case null:
            goto('login');
            break;
          case UserType.CUSTOMER:
            if (config.room.page.release.url === undefined) {
              goto('internalServerError');
              throw new Error('ROOMのURLが設定されていません。');
            }

            window.location.href = `${config.room.page.release.url}?to=select`;
            break;
          case UserType.SALESMAN:
            goto('roomList');
            break;
          default:
            throw new Error(`想定外のユーザー（ユーザー種別（${authState.currentUser?.userType}））です。`);
        }
      } else if (isNeedAuthPageAccessWithNotAuthenticated()) {
        goto('login');
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authState, location.pathname]);

  return (
    <>
      {isLoading() &&
        <LoadingPage />
      }
      {!isLoading() &&
        <AuthContext.Provider value={contextValue}>
          {children}
        </AuthContext.Provider>
      }
    </>
  );
};
